import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import apiServices from "../../../services/apiService";
import { setCredentials } from "../../../app/features/authSlice";
import storageService from "../../../utils/storageService";
import { getTenantPrefix } from "../../../utils/tenantHelper";
import { fetchAndSetTenantBranding } from "../../../services/TenantBrandingService";
import encryptedDataService from "../../../services/encryptedDataService";
import { jwtDecode } from "jwt-decode";
import apiClient from "../../../services/apiClient";
import CommonModal from "../../common/commonmodal/CommonModal";
import { faBan } from "@fortawesome/free-solid-svg-icons";

const LoginFlow = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [personType, setPersonType] = useState("1");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        fetchAndSetTenantBranding();

        const extractPartFromUrl = (url) => {
            const pattern = /^(?:https?:\/\/)?(.*?)\./;
            const match = url.match(pattern);
            return match ? match[1] : '';
        };

        const subdomain = extractPartFromUrl(window.location.href);
        if (subdomain && subdomain.trim() !== '' && subdomain !== 'localhost' && subdomain !== '127') {
            storageService.setItem('Tenant_Name', subdomain);
        }
    }, []);

    const handleLogin = async () => {
        if (!username || !password) {
            toast.warning("Please fill in all required fields");
            return;
        }

        setLoading(true);
        try {
            const body = {
                userName: username,
                password: password,
                personTypeId: Number(personType)
            };

            const res = await apiServices({
                method: "POST",
                url: "/Auth/login", // Changed from /Login/Login based on user feedback
                data: body
            });

            if (res?.success || res?.isSuccess || (res?.data && (res.data.isSuccess || res.data.success))) {
                const responseData = res?.data || res;
                const token = responseData?.token;

                if (token) {
                    const decoded = jwtDecode(token);
                    const isExpired = decoded.exp * 1000 < Date.now();

                    if (!isExpired) {
                        const userArray = responseData.Logindata || responseData.data;
                        const userData = userArray && userArray.length > 0 ? userArray[0] : null;

                        if (userData) {
                            const userRoleId = Number(userData.roleId || userData.RoleId || 0);

                            // Block Employee (roleId === 5)
                            if (userRoleId === 5) {
                                setShowAccessDeniedModal(true);
                                setLoading(false);
                                return;
                            }

                            // Store token only if not employee
                            dispatch(setCredentials({ token: token }));
                            storageService.setItem('Token', token);
                            storageService.setItem('roleName', userData.Role || userData.role || userData.RoleName || userData.roleName || userData.roles || '');
                            storageService.setItem('roleId', userData.roleId || userData.RoleId || '');
                            storageService.setItem('employeeCode', userData.Id || userData.id || userData.ID || '');
                            storageService.setItem('employeeName', userData.Name || userData.name || userData.FullName || userData.fullName || userData.UserName || userData.userName || '');
                            storageService.setItem('mobile', userData.mobileNumber || userData.MobileNumber || userData.PhoneNumber || '');
                            storageService.setItem('userImage', userData.Image || userData.image || userData.UserImage || userData.userImage || '');
                            storageService.setItem('personTypeId', personType);
                        }

                        toast.success("Login successful");

                        const tenantName = storageService.getItem("Tenant_Name");
                        const prefix = getTenantPrefix(tenantName);
                        
                        const savedRoleId = Number(userData.roleId || userData.RoleId || 0);
                        if (savedRoleId === 7 || Number(personType) === 4) {
                            navigate(`${prefix}workforce-management/staff`, { replace: true });
                        } else {
                            navigate(`${prefix}dashboard`, { replace: true });
                        }
                    } else {
                        toast.error("Token is expired");
                        storageService.removeItem('Token');
                        storageService.removeItem('Tenant_Name');
                    }
                } else {
                    toast.error(res?.message || "Login failed - No token received");
                }
            } else {
                toast.error(res?.message || res?.data?.message || "Login failed");
            }
        } catch (error) {
            console.error("Login error", error);
            toast.error("An error occurred during login");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if ((e.key === "Enter" || e.keyCode === 13) && !loading) {
            handleLogin();
        }
    };

    return (
        <>
            <div
                className="relative w-screen h-screen overflow-hidden flex flex-col justify-between p-6 md:p-10 select-none"
                style={{
                    background: "radial-gradient(circle at 5% 10%, #e0f2fe 0%, transparent 35%), radial-gradient(circle at bottom left, #fee2e2 0%, transparent 45%), radial-gradient(circle at 90% 10%, #fef2f2 0%, transparent 40%), #f8fafc"
                }}
            >
                {/* Top Bar with Brand Logo */}
                <div className="z-20 cursor-pointer" onClick={() => window.open("https://myhello.in/", "_blank")}>
                    <img
                        src="hellologo.svg"
                        alt="Hello"
                        className="h-6"
                    />
                </div>

                {/* Main Content Area */}
                <div className="flex-1 items-center z-10 w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 md:gap-12 mt-4 md:mt-0">
                    {/* Left Side: Form Content */}
                    <div className="lg:col-span-5 flex flex-col justify-center w-full max-w-[420px] mx-auto lg:mx-0 pr-0 lg:pr-8">
                        <div className="animate-in fade-in slide-in-from-left duration-500">
                            <div className="flex flex-col gap-2">
                                <div className="text-center mb-6">
                                    <h2 className="text-2xl font-bold text-[#111827]">
                                        {personType === "1" ? "User Login" : "Vendor Login"}
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Welcome back! Please enter your details.
                                    </p>
                                </div>

                                {/* Username Field */}
                                <label className="text-[#011A3B] text-xs font-semibold">Mobile Number</label>
                                <input
                                    type="text"
                                    placeholder="Enter Mobile Number"
                                    className="w-full border p-3 pl-4 rounded-lg text-xs outline-none border-gray-300 focus:border-red-500 transition-colors"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />

                                {/* Password Field */}
                                <label className="text-[#011A3B] text-xs font-semibold mt-2">Password</label>
                                <div className="relative w-full">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter Password"
                                        className="w-full border p-3 pl-4 pr-10 rounded-lg text-xs outline-none border-gray-300 focus:border-red-500 transition-colors"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(prev => !prev)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2"
                                    >
                                        <FontAwesomeIcon
                                            icon={showPassword ? faEyeSlash : faEye}
                                            className="text-gray-500 text-sm cursor-pointer"
                                        />
                                    </button>
                                </div>
                                <button
                                    disabled={loading}
                                    className={`mt-4 flex items-center justify-center bg-[#E31F26] w-full text-sm text-[#FFFFFF] font-semibold py-3 rounded-lg transition-all duration-300 ${loading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#c11a20] active:scale-[0.98]"}`}
                                    onClick={handleLogin}
                                >
                                    {loading ? (
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    ) : "Login"}
                                </button>
                                
                                {/* Toggle Login Type */}
                                <div className="mt-4 text-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPersonType(personType === "1" ? "4" : "1");
                                            setUsername("");
                                            setPassword("");
                                        }}
                                        className="text-[#E31F26] text-sm font-semibold hover:underline transition-all"
                                    >
                                        {personType === "1" ? "Are you a Vendor? Login here" : "Back to User Login"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Zentech Logo */}
                        <div className="flex justify-center items-center mt-8 cursor-pointer" onClick={() => window.open("https://zentechinfo.com/", "_blank")}>
                            <img
                                src="/zentech.svg"
                                alt="Zentech"
                                className="h-6 opacity-60 hover:opacity-80 transition-opacity object-contain"
                            />
                        </div>
                    </div>

                    {/* Right Side: Isometric Office Illustration */}
                    <div className="hidden lg:flex lg:col-span-7 h-full items-center justify-center relative p-4">
                        <img
                            src="/loginPageImg.svg"
                            alt="Workspace Illustration"
                            className="w-full h-full max-h-[75vh] object-contain select-none pointer-events-none drop-shadow-2xl animate-in fade-in zoom-in-95 duration-700"
                        />
                    </div>
                </div>
            </div>

            <CommonModal
                isOpen={showAccessDeniedModal}
                onClose={() => setShowAccessDeniedModal(false)}
                title="Access Denied"
                subTitle="Web access restriction"
                footerContent={
                    <div className="flex justify-end w-full">
                        <button
                            onClick={() => setShowAccessDeniedModal(false)}
                            className="px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                }
            >
                <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4">
                        <FontAwesomeIcon icon={faBan} className="text-3xl" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-textprimary mb-2">Web Access Not Available</h3>
                    <p className="text-sm text-gray-500 dark:text-textsecondary px-4">
                        Employees do not have access to the web portal. Please use the mobile application to access your account.
                    </p>
                </div>
            </CommonModal>
        </>
    );
};

export default LoginFlow;
